import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Loader2, Mail, Search, Download, Send, Trash2, Rocket, X, CheckCircle2,
} from "lucide-react";
import { api, apiError } from "@/lib/api";

export default function WaitlistAdmin() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [source, setSource] = useState("");
  const [showBlast, setShowBlast] = useState(false);

  const load = useCallback(async (query = "", src = "") => {
    setLoading(true);
    try {
      const params = { limit: 500 };
      if (query) params.q = query;
      if (src) params.source = src;
      const { data } = await api.get("/admin/waitlist", { params });
      setData(data);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const debounced = () => {
    clearTimeout(window.__wl_t);
    window.__wl_t = setTimeout(() => load(q, source), 400);
  };

  const remove = async (id, email) => {
    if (!window.confirm(`Remove ${email} from the waitlist?`)) return;
    try {
      await api.delete(`/admin/waitlist/${id}`);
      toast.success("Removed.");
      load(q, source);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const exportCsv = async () => {
    try {
      const res = await api.get("/admin/waitlist/export", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `bcome-waitlist-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("CSV downloaded.");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  if (loading || !data) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-[#FF7E67]" />
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="waitlist-admin">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 font-nunito text-2xl font-extrabold tracking-tight text-[#2D3748]">
            <Mail className="h-6 w-6 text-[#FF7E67]" strokeWidth={2.5} /> Waitlist
          </h2>
          <p className="mt-1 font-quicksand text-sm text-[#718096]">
            {data.total} total signups {data.blasted_count > 0 && `· ${data.blasted_count} blasted`}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={exportCsv}
            data-testid="waitlist-export-btn"
            className="inline-flex items-center gap-1.5 rounded-full border border-[#FFE8E3] bg-white px-4 py-2 font-nunito text-sm font-bold text-[#C2410C] hover:bg-[#FFF9F5]">
            <Download className="h-4 w-4" strokeWidth={2.5} /> Export CSV
          </button>
          <button onClick={() => setShowBlast(true)}
            data-testid="waitlist-blast-btn"
            className="inline-flex items-center gap-1.5 rounded-full bg-sunrise px-5 py-2 font-nunito text-sm font-bold text-white shadow-glow-coral hover:scale-[1.03]">
            <Rocket className="h-4 w-4" strokeWidth={2.5} /> Send launch email
          </button>
        </div>
      </div>

      {/* Source breakdown chips */}
      <div className="flex flex-wrap gap-2" data-testid="source-breakdown">
        <button onClick={() => { setSource(""); load(q, ""); }}
          className={`rounded-full border px-3 py-1 font-nunito text-xs font-bold ${
            !source ? "border-[#FF7E67] bg-[#FFE8E3] text-[#C2410C]"
                    : "border-[#FFE8E3] bg-white text-[#4A5568] hover:border-[#FF7E67]"}`}>
          All · {data.total}
        </button>
        {Object.entries(data.source_breakdown).map(([k, v]) => (
          <button key={k}
            onClick={() => { setSource(k); load(q, k); }}
            data-testid={`source-${k}`}
            className={`rounded-full border px-3 py-1 font-nunito text-xs font-bold ${
              source === k ? "border-[#FF7E67] bg-[#FFE8E3] text-[#C2410C]"
                           : "border-[#FFE8E3] bg-white text-[#4A5568] hover:border-[#FF7E67]"}`}>
            {k} · {v}
          </button>
        ))}
      </div>

      <div className="relative">
        <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-[#A0AEC0]" />
        <input value={q}
          onChange={(e) => { setQ(e.target.value); debounced(); }}
          placeholder="Search by email…"
          data-testid="waitlist-search"
          className="h-11 w-full rounded-full border border-[#FFE8E3] bg-white pl-11 pr-4 font-quicksand text-sm outline-none focus:border-[#FF7E67]" />
      </div>

      <div className="rounded-3xl border border-[#FFE8E3] bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="bg-[#FFF9F5]">
              <tr className="text-[#718096]">
                <th className="px-4 py-3">Email</th>
                <th className="px-4 py-3">Source</th>
                <th className="px-4 py-3">Joined</th>
                <th className="px-4 py-3 w-10"></th>
              </tr>
            </thead>
            <tbody>
              {data.rows.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-4 py-10 text-center font-quicksand text-sm text-[#718096]">
                    No signups match.
                  </td>
                </tr>
              )}
              {data.rows.map((r) => (
                <tr key={r.id} className="border-t border-[#FFE8E3] text-[#4A5568]"
                    data-testid={`row-${r.id}`}>
                  <td className="px-4 py-3 font-quicksand text-[#2D3748]">{r.email}</td>
                  <td className="px-4 py-3">
                    <span className="rounded-full bg-[#FFF4E6] px-2 py-0.5 font-nunito text-[10px] font-bold text-[#B45309]">
                      {r.source || "—"}
                    </span>
                  </td>
                  <td className="px-4 py-3 font-quicksand text-xs">
                    {r.created_at ? new Date(r.created_at).toLocaleString(undefined,
                      { month: "short", day: "numeric", year: "numeric" }) : "—"}
                  </td>
                  <td className="px-4 py-3">
                    <button onClick={() => remove(r.id, r.email)}
                      data-testid={`remove-${r.id}`}
                      title="Remove"
                      className="rounded-full p-1.5 text-[#A0AEC0] hover:bg-[#FEE2E2] hover:text-[#DC2626]">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="border-t border-[#FFE8E3] bg-[#FFF9F5] px-4 py-2 font-quicksand text-xs text-[#718096]">
          Showing {data.count} of {data.total}
        </p>
      </div>

      {showBlast && (
        <BlastModal
          total={data.total}
          blasted={data.blasted_count}
          sources={data.source_breakdown}
          onClose={() => setShowBlast(false)}
          onSent={() => { setShowBlast(false); load(q, source); }}
        />
      )}
    </div>
  );
}

function BlastModal({ total, blasted, sources, onClose, onSent }) {
  const [subject, setSubject] = useState("b→come is live — you're in early!");
  const [intro, setIntro] = useState(
    "<p>We just opened the doors. Because you joined the waitlist, you're at the front of the line.</p>" +
    "<p>Head over to b→come now, create your account, and start building your Life Blueprint.</p>"
  );
  const [ctaLabel, setCtaLabel] = useState("Get my early access");
  const [ctaUrl, setCtaUrl] = useState("/register");
  const [onlySource, setOnlySource] = useState("");
  const [dryRun, setDryRun] = useState(true);
  const [testEmail, setTestEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  const remaining = total - blasted;

  const submit = async () => {
    if (dryRun && !testEmail.trim()) {
      toast.error("Add a test email for the dry run.");
      return;
    }
    if (!dryRun && !window.confirm(
      `You are about to send this to ${remaining} un-blasted recipients${
        onlySource ? ` in "${onlySource}"` : ""}. Continue?`)) return;
    setSending(true);
    try {
      const { data } = await api.post("/admin/waitlist/launch-blast", {
        subject,
        intro,
        cta_label: ctaLabel,
        cta_url: ctaUrl,
        dry_run: dryRun,
        test_email: dryRun ? testEmail : null,
        only_source: onlySource || null,
      });
      setResult(data);
      if (!dryRun && data.sent > 0) {
        toast.success(`Sent to ${data.sent} of ${data.total}.`);
        setTimeout(onSent, 1400);
      } else if (dryRun && data.sent > 0) {
        toast.success(`Test email sent to ${testEmail}.`);
      } else {
        toast.error(`No emails sent (failed: ${data.failed}). Check EMERGENT_EMAIL_KEY.`);
      }
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSending(false); }
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-end justify-center bg-black/40 backdrop-blur-sm sm:items-center"
      onClick={onClose} data-testid="blast-modal">
      <div className="max-h-[92vh] w-full max-w-2xl overflow-y-auto rounded-t-3xl bg-white p-6 sm:rounded-3xl"
        onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 flex items-start justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.28em] text-[#C2410C]">Launch email</p>
            <h3 className="mt-1 font-nunito text-2xl font-extrabold text-[#2D3748]">
              Send announcement
            </h3>
            <p className="mt-1 font-quicksand text-sm text-[#718096]">
              {remaining} of {total} still to receive an email
              {blasted > 0 && ` (${blasted} already sent earlier)`}.
            </p>
          </div>
          <button onClick={onClose} className="rounded-full p-2 text-[#718096] hover:bg-[#FFF9F5]">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="mb-1 block font-nunito text-xs font-bold uppercase tracking-wide text-[#718096]">
              Subject
            </label>
            <input value={subject} onChange={(e) => setSubject(e.target.value)}
              data-testid="blast-subject"
              className="h-11 w-full rounded-2xl border border-[#FFE8E3] bg-[#FFF9F5] px-4 font-nunito text-sm outline-none focus:border-[#FF7E67]" />
          </div>
          <div>
            <label className="mb-1 block font-nunito text-xs font-bold uppercase tracking-wide text-[#718096]">
              Message HTML
            </label>
            <textarea value={intro} onChange={(e) => setIntro(e.target.value)}
              rows={6}
              data-testid="blast-intro"
              className="w-full rounded-2xl border border-[#FFE8E3] bg-[#FFF9F5] px-4 py-3 font-quicksand text-sm outline-none focus:border-[#FF7E67]" />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1 block font-nunito text-xs font-bold uppercase tracking-wide text-[#718096]">
                CTA button label
              </label>
              <input value={ctaLabel} onChange={(e) => setCtaLabel(e.target.value)}
                data-testid="blast-cta-label"
                className="h-11 w-full rounded-2xl border border-[#FFE8E3] bg-[#FFF9F5] px-4 font-nunito text-sm outline-none focus:border-[#FF7E67]" />
            </div>
            <div>
              <label className="mb-1 block font-nunito text-xs font-bold uppercase tracking-wide text-[#718096]">
                CTA URL / path
              </label>
              <input value={ctaUrl} onChange={(e) => setCtaUrl(e.target.value)}
                data-testid="blast-cta-url"
                className="h-11 w-full rounded-2xl border border-[#FFE8E3] bg-[#FFF9F5] px-4 font-nunito text-sm outline-none focus:border-[#FF7E67]" />
            </div>
          </div>

          <div>
            <label className="mb-1 block font-nunito text-xs font-bold uppercase tracking-wide text-[#718096]">
              Only send to source (optional)
            </label>
            <select value={onlySource} onChange={(e) => setOnlySource(e.target.value)}
              data-testid="blast-source-filter"
              className="h-11 w-full rounded-2xl border border-[#FFE8E3] bg-[#FFF9F5] px-4 font-nunito text-sm outline-none focus:border-[#FF7E67]">
              <option value="">— Everyone —</option>
              {Object.entries(sources).map(([k, v]) => (
                <option key={k} value={k}>{k} ({v})</option>
              ))}
            </select>
          </div>

          <label className="flex items-center gap-2 rounded-2xl border border-dashed border-[#FFE8E3] bg-[#FFF9F5] p-3">
            <input type="checkbox" checked={dryRun} onChange={(e) => setDryRun(e.target.checked)}
              data-testid="blast-dry-run"
              className="h-4 w-4 accent-[#FF7E67]" />
            <span className="flex-1 font-quicksand text-xs text-[#4A5568]">
              <strong className="font-bold text-[#2D3748]">Dry run</strong> — send only to the test email below (no real sends).
            </span>
          </label>
          {dryRun && (
            <input value={testEmail} onChange={(e) => setTestEmail(e.target.value)}
              placeholder="Test email address"
              data-testid="blast-test-email"
              className="h-11 w-full rounded-2xl border border-[#FFE8E3] bg-[#FFF9F5] px-4 font-nunito text-sm outline-none focus:border-[#FF7E67]" />
          )}

          {result && (
            <div className={`flex items-start gap-2 rounded-2xl border p-3 ${
              result.sent > 0 ? "border-[#D1FAE5] bg-[#E6FBF5]/50" : "border-[#FEE2E2] bg-[#FEF2F2]"}`}
              data-testid="blast-result">
              <CheckCircle2 className={`mt-0.5 h-4 w-4 ${result.sent > 0 ? "text-[#06D6A0]" : "text-[#DC2626]"}`} strokeWidth={2.5} />
              <div className="flex-1 font-quicksand text-xs">
                <span className="font-bold text-[#2D3748]">Result: </span>
                sent {result.sent} · failed {result.failed} · total {result.total}
                {result.dry_run && " · (dry run)"}
                {result.failed > 0 && (
                  <p className="mt-1 text-[#7F1D1D]">
                    Sends are failing at the email proxy — verify EMERGENT_EMAIL_KEY on the backend.
                  </p>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="mt-5 flex gap-2">
          <button onClick={onClose}
            className="rounded-full border border-[#FFE8E3] px-4 py-2 font-nunito text-sm font-bold text-[#718096] hover:bg-[#FFF9F5]">
            Close
          </button>
          <button onClick={submit} disabled={sending}
            data-testid="blast-send-btn"
            className="flex flex-1 items-center justify-center gap-2 rounded-full bg-sunrise py-3 font-nunito text-sm font-bold text-white shadow-glow-coral disabled:opacity-60">
            {sending
              ? <><Loader2 className="h-4 w-4 animate-spin" /> Sending…</>
              : (
                <>
                  <Send className="h-4 w-4" strokeWidth={2.5} />
                  {dryRun ? "Send test email" : `Send to ${remaining} people`}
                </>
              )}
          </button>
        </div>
      </div>
    </div>
  );
}
